import { ParticipantProfile } from "../../../models/entities/participant.model";
import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import ParticipantDetailsCard from "./ParticipantDetailsCard";
import {
	PtPhysicianInfo,
	PtPhysiciansInfoForm,
} from "../../../models/pages/participant-details/physicians-info.model";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { FormApi } from "final-form";
import { PhysiciansUpdateBody } from "../../../models/endpoints/physicians-endpoint.model";
import { PtDetailsMode } from "../../../models/pages/participant-details/participant-details.model";
import UiButton from "../../../components/button/Button";
import UiInputText from "../../../components/input-text/InputText.component";
import { Validations } from "../../../services/form/validations.service";
import ParticipantDetailsField from "./ParticipantDetailsField";
import { TbTrash } from "react-icons/tb";
import { Formatter } from "../../../services/formatter/formatter.service";
import {
	PhysicianSpecialty,
	PhysicianSharingMethod,
} from "../../../models/entities/physician.model";
import UiSelect from "../../../components/select/Select.component";
import { ListOption } from "../../../models/misc.model";
import FormUiInputSwitch from "../../../components/input-switch/InputSwitch.component";
import { useTranslation } from "react-i18next";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import UiInputNumber from "../../../components/input-number/InputNumber.component";

// Format physicians for form display, strip +1 prefix from fax
const formatPhysicians = (physicians: PtPhysicianInfo[]): PtPhysicianInfo[] => {
	return physicians.map((x) => {
		const physician = { ...x };
		if (physician.fax?.startsWith("+1")) {
			physician.fax = physician.fax.replace(/^\+1/, "");
		}
		return physician;
	});
};

const ParticipantDetailsPhysicians = ({
	participant,
	participantContext,
}: {
	participant: ParticipantProfile;
	participantContext: ParticipantContextProps;
}) => {
	const { t } = useTranslation("common");
	const [mode, setMode] = useState<PtDetailsMode>(PtDetailsMode.READ);
	const [showError, setShowError] = useState<boolean>(false);
	const [formData, setFormData] = useState<PtPhysiciansInfoForm>({
		data: [],
	});

	useEffect(() => {
		setFormData({
			data: participant.physicians ? formatPhysicians(participant.physicians) : [],
		});
	}, [participant.physicians]);

	const add = () => {
		const data = [...formData.data];
		data.push(
			new PtPhysicianInfo({
				participantId: participant.id,
				deleted: false,
			})
		);
		setFormData({ data: [...data] });
	};

	const remove = (id: number | null, index: number) => {
		if (id !== null) {
			setFormData({
				data: formData.data.map((x) => {
					if (id === x.id) {
						x.deleted = true;
					}
					return x;
				}),
			});
		} else {
			const data = [...formData.data];
			data.splice(index, 1);
			setFormData({
				data: [...data],
			});
		}
	};

	const hasChanged = (formItem: PtPhysicianInfo) => {
		let result = false;

		// Find
		if (formItem.id && participant.physicians) {
			const ph = participant.physicians.find((x) => x.id === formItem.id);
			if (
				ph &&
				(ph.name !== formItem.name ||
					ph.email !== formItem.email ||
					ph.phone !== formItem.phone ||
					ph.fax !== formItem.fax ||
					ph.address !== formItem.address ||
					ph.specialty !== formItem.specialty ||
					ph.sendHealthSummaryReport !== formItem.sendHealthSummaryReport ||
					ph.preferredSharingMethod !== formItem.preferredSharingMethod ||
					ph.verified !== formItem.verified)
			) {
				result = true;
			}
		}

		return result;
	};

	const setBody = (): PhysiciansUpdateBody => {
		const result: PhysiciansUpdateBody = [];
		formData.data.forEach((x) => {
			if (x.deleted || !x.id || hasChanged(x)) {
				// Create a copy of the physician data with formatted fax
				const physicianData = { ...x };

				// Add +1 prefix to fax if it exists and doesn't already have it
				if (physicianData.fax && !physicianData.fax.startsWith("+1")) {
					physicianData.fax = `+1${physicianData.fax}`;
				}

				result.push(physicianData);
			}
		});
		return result;
	};

	const handleSubmit = async (
		data: PtPhysiciansInfoForm,
		form: FormApi<PtPhysiciansInfoForm, Partial<PtPhysiciansInfoForm>>
	) => {
		setShowError(false);
		await EndpointsService.dataRetriever
			.updatePhysicians({
				participantId: participant.id,
				body: setBody(),
			})
			.then(() => {
				form.reset();
				participantContext.onRefresh();
				// Update form mode
				setMode(PtDetailsMode.READ);
			})
			.catch(() => setShowError(true));
	};

	return (
		<Form
			onSubmit={handleSubmit}
			initialValues={formData}
			render={({ handleSubmit, form, submitting }) => (
				<form onSubmit={handleSubmit}>
					<ParticipantDetailsCard
						title="PARTICIPANT.DETAILS.PHYSICIANS.TITLE"
						submitting={submitting}
						mode={mode}
						enableEditing={true}
						showError={showError}
						// enableSubmit={!!Object.keys(setRequest())}
						enableSubmit={form.getState().valid}
						onCancel={() => {
							form.reset();
							setShowError(false);
							const resetData = participant.physicians
								? formatPhysicians(participant.physicians)
								: [];
							setFormData({ data: resetData });
							setMode(PtDetailsMode.READ);
						}}
						onModeChange={() => setMode(PtDetailsMode.UPDATE)}
					>
						{/* DATA */}
						{formData.data.length > 0 ? (
							<>
								{formData.data
									// .filter((x) => !x.deleted) // NOT WORKING (WHY??)
									.map((x, index) => {
										return !x.deleted ? (
											<div
												id={index.toString()}
												key={index.toString()}
											>
												<div className="row align-items-center">
													{/* Form fields */}
													<div className="col-10 col-sm-11 col-md-11 col-lg-11">
														<div className="row">
															{/* Name */}
															<div className="col-12 col-sm-6 col-md-4 col-lg-4">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.name"
																		value={x.name}
																	/>
																) : (
																	<UiInputText
																		id={`data[${index}].name`}
																		name={`data[${index}].name`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.name"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].name =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		validations={[
																			Validations.required,
																		]}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Email */}
															<div className="col-12 col-sm-6 col-md-5 col-lg-5">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.email"
																		value={x.email}
																	/>
																) : (
																	<UiInputText
																		id={`data[${index}].email`}
																		name={`data[${index}].email`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.email"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].email =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		validations={[]}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Phone */}
															<div className="col-12 col-sm-6 col-md-3 col-lg-3">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.phone"
																		value={
																			x.phone
																				? Formatter.phoneNumber(
																						x.phone
																					)
																				: Formatter.NO_DATA
																		}
																	/>
																) : (
																	<UiInputText
																		id={`data[${index}].phone`}
																		name={`data[${index}].phone`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.phone"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].phone =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		validations={
																			formData.data[index]
																				.phone
																				? [
																						Validations.regex(
																							/^\d{10}$/,
																							"PARTICIPANT.DETAILS.PHYSICIANS.MSG_VALIDATION_PHONE"
																						),
																					]
																				: []
																		}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Fax */}
															<div className="col-12 col-sm-6 col-md-3 col-lg-3">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.fax"
																		value={
																			x.fax
																				? Formatter.phoneNumber(
																						// Show full number with +1 in read mode
																						x.fax.startsWith(
																							"+1"
																						)
																							? x.fax
																							: `+1${x.fax}`
																					)
																				: Formatter.NO_DATA
																		}
																	/>
																) : (
																	<UiInputNumber
																		id={`data[${index}].fax`}
																		name={`data[${index}].fax`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.fax"
																		prefix="+1 "
																		useGrouping={false}
																		value={
																			x.fax
																				? parseInt(x.fax)
																				: undefined
																		}
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].fax =
																				e.value
																					? e.value.toString()
																					: null;
																			setFormData({
																				data,
																			});
																		}}
																		validations={
																			formData.data[index].fax
																				? [
																						Validations.numbers,
																					]
																				: []
																		}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Address */}
															<div className="col-12 col-sm-6 col-md-9 col-lg-9">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.address"
																		value={x.address}
																	/>
																) : (
																	<UiInputText
																		id={`data[${index}].address`}
																		name={`data[${index}].address`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.address"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].address =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Specialty */}
															<div className="col-12 col-sm-6 col-md-6 col-lg-6">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.specialty"
																		value={
																			x.specialty
																				? t(
																						`ENUMS.PHYSICIAN_SPECIALTY.${x.specialty}`
																					)
																				: x.specialty
																		}
																	/>
																) : (
																	<UiSelect
																		id={`data[${index}].specialty`}
																		name={`data[${index}].specialty`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.specialty"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].specialty =
																				e.target
																					.value as PhysicianSpecialty | null;
																			setFormData({
																				data,
																			});
																		}}
																		disabled={submitting}
																		filter={true}
																		showClear={true}
																		options={Object.keys(
																			PhysicianSpecialty
																		).map(
																			(x) =>
																				new ListOption({
																					id: x,
																					label: `ENUMS.PHYSICIAN_SPECIALTY.${x}`,
																				})
																		)}
																	/>
																)}
															</div>

															{/* Preferred sharing method */}
															<div className="col-12 col-sm-6 col-md-6 col-lg-6">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.preferredSharingMethod"
																		value={
																			x.preferredSharingMethod
																				? t(
																						`ENUMS.PREFERRED_SHARING_METHOD.${x.preferredSharingMethod}`
																					)
																				: x.preferredSharingMethod
																		}
																	/>
																) : (
																	<UiSelect
																		id={`data[${index}].preferredSharingMethod`}
																		name={`data[${index}].preferredSharingMethod`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.preferredSharingMethod"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[
																				index
																			].preferredSharingMethod =
																				e.target
																					.value as PhysicianSharingMethod | null;
																			setFormData({
																				data,
																			});
																		}}
																		disabled={submitting}
																		filter={true}
																		showClear={true}
																		options={Object.keys(
																			PhysicianSharingMethod
																		).map(
																			(x) =>
																				new ListOption({
																					id: x,
																					label: `ENUMS.PREFERRED_SHARING_METHOD.${x}`,
																				})
																		)}
																	/>
																)}
															</div>

															{/* Send Health Summary Report */}
															<div className="col-12 col-sm-6 col-md-6 col-lg-6">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.sendHealthSummaryReport"
																		value={
																			x.sendHealthSummaryReport
																		}
																	/>
																) : (
																	<FormUiInputSwitch
																		id={`data[${index}].sendHealthSummaryReport`}
																		name={`data[${index}].sendHealthSummaryReport`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.sendHealthSummaryReport"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[
																				index
																			].sendHealthSummaryReport =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Information verified */}
															<div className="col-12 col-sm-6 col-md-6 col-lg-6">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.verified"
																		value={x.verified}
																	/>
																) : (
																	<FormUiInputSwitch
																		id={`data[${index}].verified`}
																		name={`data[${index}].verified`}
																		label="PARTICIPANT.DETAILS.PHYSICIANS.FORM_FIELDS.verified"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].verified =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		disabled={submitting}
																	/>
																)}
															</div>
														</div>
													</div>

													{/* Remove form */}
													<div className="col-2 col-sm-1 col-md-1 col-lg-1 text-end">
														<UiButton
															className="p-button-text neutral"
															type="button"
															icon={
																<TbTrash
																	style={{ fontSize: "1.5rem" }}
																/>
															}
															disabled={
																submitting ||
																mode === PtDetailsMode.READ
															}
															onClick={() => remove(x.id, index)}
														/>
													</div>
												</div>

												<hr />
											</div>
										) : (
											<></>
										);
									})}
							</>
						) : (
							<></>
						)}

						<div className="d-flex justify-content-center mt-2">
							<UiButton
								id="addPhysician"
								type="button"
								className="p-button-sm p-button-rounded"
								label="PARTICIPANT.DETAILS.PHYSICIANS.CREATE"
								icon={"pi pi-plus"}
								onClick={() => add()}
								disabled={submitting || mode === PtDetailsMode.READ}
							/>
						</div>
					</ParticipantDetailsCard>
				</form>
			)}
		/>
	);
};

export default ParticipantDetailsPhysicians;
